import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { i18n as I18nInstance } from 'i18next';
import formatNumber from '/shared/functions/formatNumber';

import Boilers from '/business/airbp/images/icons/Boilers.svg';
import { initiativeVisuals } from '/business/shared/components/questions/helper/initiativeVisuals';
import type { BasketTableAnswerItem, QuestionConfigQuestionType } from '/index';
import useBoilersAndFurnacesSchema from '/business/shared/components/questions/scope1/_hooks/useBoilersAndFurnacesSchema';
import boilersCard from '/business/shared/images/initiatives/boilers-card-1x.png';
import boilerBackground from '/business/shared/images/initiatives/boiler-banner-1x.png';

import educationContentJson from '/business/shared/components/questions/scope1/_education/boilers-and-furnaces.json';
import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';

export const getConfigureBoilersAndFurnaces = ({
  i18n,
  reductionCardsSlides = ['slide1'],
}: {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides: string[];
}): QuestionConfigQuestionType => {
  return {
    id: 'boilers-and-furnaces',
    useHookLoader: useBoilersAndFurnacesSchema,
    scopeCategory: questionScopeCategories.SCOPE_1,
    isCompletePropName: 'isBoilersAndFurnacesComplete',
    title: i18n.t('business:Questions.calculations.boilersAndFurnaces.title'),
    primaryI18nKey: 'business:QuestionPage.forms.boilersAndFurnaces.key',
    subTextI18nKey: 'business:QuestionPage.forms.boilersAndFurnaces.tip',
    apiKey: 'calculation-boilers-and-furnaces',
    initiative: {
      getVisuals: ({ frontendEnum, questionTypeKey = null, isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: <>test</>,
            background: boilerBackground,
            useMask: false,
            cardBackground: boilersCard,
          };
        }
        if (isBpOffering) {
          const { cardImage, headerImage } = initiativeVisuals(frontendEnum, questionTypeKey);
          return {
            description: <>test1</>,
            background: headerImage,
            useMask: false,
            cardBackground: cardImage,
          };
        }
      },
      formatForInitiativeSummary: ({ initiativeAnswerEnum = null } = {}) => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: true,
          category: initiativeAnswerEnum,
          description: `Remaining ${initiativeAnswerEnum} consumption for fuels burned on-site to reduce after incorporating the initiatives already added to the plan so far`,
          descriptionNoReduction: `${initiativeAnswerEnum} consumption for fuels burned on-site during the reporting period `,
        };
      },
    },
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.boilersAndFurnaces.key',
    icon: Boilers,
    reductionPageKey: 'boilersAndFurnaces',
    reductionPageChartTitle: i18n.t('business:QuestionPage.forms.boilersAndFurnaces.key'),
    reductionPageCards: reductionCardsSlides,
    table: {
      title: i18n.t('business:Questions.calculations.boilersAndFurnaces.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.question3.table.heading1'),
        i18n.t('business:Questions.calculations.question3.table.heading2'),
        i18n.t('business:Questions.calculations.question3.table.heading3'),
      ],
      cells: [
        'fuelType',
        ({
          quantity,
          fuelUnit = 'units',
          harmonisedInjection,
        }: {
          harmonisedInjection: (fuelUnit: string) => string;
          quantity: number;
          fuelUnit: string;
        }) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)} ${harmonisedInjection(fuelUnit)}`,
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: false,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'fuelType',
      items: [
        'fuelType',
        ({ quantity, fuelUnit = 'units' }: { quantity: number; fuelUnit: string }) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)}`,
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question3.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with fuels used in boilers and furnaces by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
        },
        {
          title: questionSelectorOptions.SUPPLIER,
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1({
        questionFormatName: 'boilers-and-furnaces',
        emissionSourcePlaceholder: 'Select type of fuel',
      }),
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'boilers-and-furnaces' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuels that have been used in boilers and furnaces by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What fuels have been used in boilers and furnaces by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
